import { useState, useEffect, useCallback } from "react";
import React from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Play, RefreshCw, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const FACTOR_COLORS = {
  market: "#64748b", size: "#f59e0b", momentum: "#3b82f6", value: "#10b981",
  quality: "#8b5cf6", volatility: "#ef4444", beta: "#ec4899",
};

const fmtZ = (v) => (v === null || v === undefined ? "—" : v.toFixed(2));
const fmtCap = (v) => {
  if (!v) return "—";
  if (v >= 1e12) return `$${(v / 1e12).toFixed(2)}T`;
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  return `$${(v / 1e6).toFixed(0)}M`;
};

export const FactorsV2Tab = ({ token }) => {
  const [overview, setOverview] = useState(null);
  const [series, setSeries] = useState({ series: [], factors: [] });
  const [expo, setExpo] = useState({ rows: [], total: 0, sectors: [] });
  const [status, setStatus] = useState(null);
  const [search, setSearch] = useState("");
  const [sector, setSector] = useState("");
  const [sortBy, setSortBy] = useState("market_cap");
  const [sortDir, setSortDir] = useState(-1);
  const [starting, setStarting] = useState(false);

  const hdrs = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    try {
      const [o, s, st] = await Promise.all([
        fetch(`${API}/factors-v2/overview`, { headers: hdrs }).then((r) => r.json()),
        fetch(`${API}/factors-v2/factor-returns`, { headers: hdrs }).then((r) => r.json()),
        fetch(`${API}/factors-v2/status`, { headers: hdrs }).then((r) => r.json()),
      ]);
      setOverview(o);
      setSeries(s);
      setStatus(st);
    } catch (e) { /* noop */ }
  }, [token]);

  const fetchExposures = useCallback(async () => {
    try {
      const params = new URLSearchParams({ sort_by: sortBy, direction: sortDir, search, sector, limit: 200 });
      const r = await fetch(`${API}/factors-v2/exposures?${params}`, { headers: hdrs }).then((x) => x.json());
      setExpo(r);
    } catch (e) { /* noop */ }
  }, [token, sortBy, sortDir, search, sector]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { fetchExposures(); }, [fetchExposures]);

  useEffect(() => {
    if (status?.status !== "running" && !status?.task_alive) return;
    const t = setInterval(async () => {
      const st = await fetch(`${API}/factors-v2/status`, { headers: hdrs }).then((r) => r.json());
      setStatus(st);
      if (st.status === "completed" || st.status === "failed") {
        fetchAll(); fetchExposures();
        if (st.status === "completed") toast.success("Factor v2 build complete");
        if (st.status === "failed") toast.error(`Build failed: ${st.error}`);
      }
    }, 4000);
    return () => clearInterval(t);
  }, [status?.status, status?.task_alive]);

  const runPipeline = async (mode) => {
    setStarting(true);
    try {
      const r = await fetch(`${API}/factors-v2/run?mode=${mode}`, { method: "POST", headers: hdrs });
      if (r.status === 409) { toast.warning("Pipeline already running"); }
      else if (r.ok) {
        toast.success(`${mode === "full" ? "Full build" : "Incremental update"} started`);
        setStatus({ status: "running", task_alive: true, phase: "starting" });
      }
    } finally { setStarting(false); }
  };

  const sortClick = (col) => {
    if (sortBy === col) setSortDir(-sortDir);
    else { setSortBy(col); setSortDir(-1); }
  };

  const running = status?.status === "running" || status?.task_alive;
  const meta = overview?.meta || {};

  const cols = [
    ["symbol", "Symbol"], ["market_cap", "MCap"], ["size", "Size"],
    ["momentum", "Mom"], ["value", "Value"], ["quality", "Quality"],
    ["volatility", "Vol"], ["beta", "Beta z"], ["beta_raw", "β Raw"],
    ["bp", "B/P"], ["ey", "E/P"], ["roe", "ROE"], ["specific_vol_ann", "SpecVol"],
  ];

  return (
    <div className="space-y-4" data-testid="factors-v2-tab">
      {/* Header / controls */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="text-base">Barra-Style Characteristic Model</CardTitle>
              <CardDescription className="text-xs mt-1">
                Exposures from firm characteristics (z-scored daily) · factor returns via cap-weighted
                cross-sectional WLS with sector-neutrality constraint · point-in-time fundamentals (+45d lag)
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={running || starting}
                onClick={() => runPipeline("incremental")} data-testid="factors-v2-run-incremental">
                {running ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
                Update
              </Button>
              <Button size="sm" disabled={running || starting}
                onClick={() => runPipeline("full")} data-testid="factors-v2-run-full">
                <Play className="h-3.5 w-3.5 mr-1" /> Full Build
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground" data-testid="factors-v2-meta">
            <span>Status: <Badge variant={running ? "default" : status?.status === "failed" ? "destructive" : "secondary"}
              className="text-[10px] ml-1" data-testid="factors-v2-status-badge">
              {running ? `running · ${status?.phase || ""}` : status?.status || "never run"}</Badge></span>
            {running && status?.phase === "fundamentals" && (
              <span>Fundamentals: {status?.progress || 0}/{status?.total || 0}</span>
            )}
            {running && status?.message && <span>{status.message}</span>}
            {meta.last_date && <span>Last date: <b>{meta.last_date}</b></span>}
            {meta.n_dates > 0 && <span>History: {meta.n_dates} days ({meta.first_date} →)</span>}
            {meta.mean_r2 != null && <span>Mean cross-sectional R²: <b>{(meta.mean_r2 * 100).toFixed(1)}%</b></span>}
            {meta.avg_universe && <span>Avg universe: {meta.avg_universe} stocks</span>}
            {status?.last_build?.fund_coverage != null && (
              <span>Fundamentals coverage: {(status.last_build.fund_coverage * 100).toFixed(0)}%</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Cumulative factor returns */}
      {series.series.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Cumulative Factor Returns (%)</CardTitle>
            <CardDescription className="text-xs">
              Return to a portfolio with unit exposure to each style, zero exposure to all others (market/sector-purified)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-72" data-testid="factors-v2-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={series.series} margin={{ top: 5, right: 10, bottom: 0, left: -15 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} minTickGap={60} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ fontSize: 11 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {series.factors.map((f) => (
                    <Line key={f} type="monotone" dataKey={f} stroke={FACTOR_COLORS[f] || "#888"}
                      dot={false} strokeWidth={f === "market" ? 2 : 1.4} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Factor stats */}
      {overview?.factor_stats?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Factor Return Statistics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="factors-v2-stats-table">
                <thead>
                  <tr className="border-b text-muted-foreground">
                    <th className="text-left py-1.5 pr-4">Factor</th>
                    <th className="text-right py-1.5 px-3">Ann. Return</th>
                    <th className="text-right py-1.5 px-3">Ann. Vol</th>
                    <th className="text-right py-1.5 px-3">Sharpe</th>
                    <th className="text-right py-1.5 px-3">t-stat</th>
                  </tr>
                </thead>
                <tbody>
                  {overview.factor_stats.map((s, i) => {
                    const isSec = s.factor.startsWith("sec_");
                    const firstSec = isSec && !overview.factor_stats[i - 1]?.factor.startsWith("sec_");
                    return (
                    <React.Fragment key={s.factor}>
                    {firstSec && (
                      <tr className="border-b">
                        <td colSpan={5} className="py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                          Sector factors (cap-weighted zero-sum)
                        </td>
                      </tr>
                    )}
                    <tr className={`border-b border-border/40 ${isSec ? "text-muted-foreground" : ""}`}>
                      <td className="py-1.5 pr-4 font-medium">
                        <span className="inline-block w-2 h-2 rounded-full mr-2"
                          style={{ background: isSec ? "#3f3f46" : FACTOR_COLORS[s.factor] || "#888" }} />
                        {isSec ? s.factor.slice(4) : s.factor}
                      </td>
                      <td className={`text-right px-3 font-mono ${s.ann_return_pct > 0 ? "text-emerald-600" : "text-red-500"}`}>
                        {s.ann_return_pct > 0 ? "+" : ""}{s.ann_return_pct}%
                      </td>
                      <td className="text-right px-3 font-mono">{s.ann_vol_pct}%</td>
                      <td className="text-right px-3 font-mono">{s.sharpe ?? "—"}</td>
                      <td className={`text-right px-3 font-mono ${Math.abs(s.t_stat) >= 2 ? "font-semibold" : ""}`}>
                        {s.t_stat ?? "—"}
                      </td>
                    </tr>
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Exposures table */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="text-sm">Latest Exposures {meta.last_date ? `· ${meta.last_date}` : ""}</CardTitle>
              <CardDescription className="text-xs">
                Cross-sectional z-scores ({expo.total} stocks) — click a column to sort
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Input placeholder="Search symbol…" value={search} onChange={(e) => setSearch(e.target.value)}
                className="h-8 w-36 text-xs" data-testid="factors-v2-search" />
              <Select value={sector || "__all__"} onValueChange={(v) => setSector(v === "__all__" ? "" : v)}>
                <SelectTrigger className="h-8 w-44 text-xs" data-testid="factors-v2-sector-select">
                  <SelectValue placeholder="All sectors" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All sectors</SelectItem>
                  {expo.sectors.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
            <table className="w-full text-xs" data-testid="factors-v2-exposures-table">
              <thead className="sticky top-0 bg-background">
                <tr className="border-b text-muted-foreground">
                  {cols.map(([key, label]) => (
                    <th key={key} onClick={() => sortClick(key)}
                      className={`py-1.5 px-2 cursor-pointer hover:text-foreground whitespace-nowrap ${key === "symbol" ? "text-left" : "text-right"}`}>
                      {label}{sortBy === key ? (sortDir === -1 ? " ↓" : " ↑") : ""}
                    </th>
                  ))}
                  <th className="text-left py-1.5 px-2">Sector</th>
                </tr>
              </thead>
              <tbody>
                {expo.rows.map((r) => (
                  <tr key={r.symbol} className="border-b border-border/40 hover:bg-muted/40"
                    data-testid={`factors-v2-row-${r.symbol}`}>
                    <td className="py-1 px-2 font-medium">{r.symbol}</td>
                    <td className="text-right px-2 font-mono">{fmtCap(r.market_cap)}</td>
                    {["size", "momentum", "value", "quality", "volatility", "beta"].map((k) => (
                      <td key={k} className={`text-right px-2 font-mono ${r[k] > 1 ? "text-emerald-600" : r[k] < -1 ? "text-red-500" : ""}`}>
                        {fmtZ(r[k])}
                      </td>
                    ))}
                    <td className="text-right px-2 font-mono font-semibold">{fmtZ(r.beta_raw)}</td>
                    <td className="text-right px-2 font-mono">{r.bp ?? "—"}</td>
                    <td className="text-right px-2 font-mono">{r.ey ?? "—"}</td>
                    <td className="text-right px-2 font-mono">{r.roe != null ? `${(r.roe * 100).toFixed(0)}%` : "—"}</td>
                    <td className="text-right px-2 font-mono">{r.specific_vol_ann != null ? `${(r.specific_vol_ann * 100).toFixed(0)}%` : "—"}</td>
                    <td className="px-2 whitespace-nowrap">{r.sector}</td>
                  </tr>
                ))}
                {expo.rows.length === 0 && (
                  <tr><td colSpan={14} className="text-center py-6 text-muted-foreground">
                    No exposures yet — run a Full Build
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
